import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Camera, Upload, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

/**
 * MobilePhotoCropModal - mobile-first square photo cropper for Mundalo.
 *
 * Why custom (not Cloudinary widget)?
 *   The widget's crop UI overflows the 390px viewport, hides the Confirm
 *   button below the fold, and has no pinch-to-zoom. This component:
 *
 *   • Full-screen on mobile, centered modal on ≥768px
 *   • Always-visible sticky bottom action bar (Cancel · Use photo)
 *   • Pinch-to-zoom on touch + wheel-to-zoom on desktop
 *   • Drag-to-pan with momentum-free, clamped translation
 *   • 1:1 square crop enforced (visible corner handles)
 *   • Client-side auto-compress to ≤500 KB JPEG before upload
 *   • Direct unsigned upload to Cloudinary
 *
 * Props:
 *   - open: bool
 *   - file: File | null
 *   - onClose(): close without saving
 *   - onUploaded(secureUrl): called with Cloudinary URL after success
 *   - cloudName: string (REACT_APP_CLOUDINARY_CLOUD_NAME)
 *   - uploadPreset: string (REACT_APP_CLOUDINARY_UPLOAD_PRESET)
 *   - folder: string (default 'mundalo_profile_photos')
 *   - outputSize: pixel side of final square (default 512)
 *   - maxKB: hard ceiling in KB (default 500)
 */
const CROP_BOX_DESKTOP_PX = 360; // crop box on ≥md
const CROP_BOX_MOBILE_VW = 86;   // 86 % of viewport width on mobile (e.g. ~335px on 390px screen)
const MIN_SCALE = 1;
const MAX_SCALE = 4;

const distance = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

const MobilePhotoCropModal = ({
  open,
  file,
  onClose,
  onUploaded,
  cloudName,
  uploadPreset,
  folder = 'mundalo_profile_photos',
  outputSize = 512,
  maxKB = 500,
  aspect = 1,                      // 1 = square, null = no crop (community posts)
  title = 'Crop profile photo',
}) => {
  const cropAreaRef = useRef(null);
  const imgRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [boxPx, setBoxPx] = useState(CROP_BOX_DESKTOP_PX);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);  // 0..100
  const [err, setErr] = useState(null);

  // touch/pointer state
  const drag = useRef(null);    // {sx, sy, startX, startY}
  const pinch = useRef(null);   // {startDist, startScale}

  // ---------- LIFECYCLE ----------

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setScale(1);
    setPos({ x: 0, y: 0 });

    // Compute crop-box size responsive to viewport.
    const compute = () => {
      const vw = window.innerWidth;
      if (vw < 768) setBoxPx(Math.round(vw * (CROP_BOX_MOBILE_VW / 100)));
      else setBoxPx(CROP_BOX_DESKTOP_PX);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [open]);

  // Lock body scroll while open (mobile Safari rubber-band fix).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Load image from File → object URL.
  useEffect(() => {
    if (!file) { setImgUrl(null); return; }
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    setImgNatural({ w: el.naturalWidth, h: el.naturalHeight });
    // Initial fit: scale so the SMALLER side matches the crop box (covers fully).
    const minSide = Math.min(el.naturalWidth, el.naturalHeight);
    const initial = boxPx / minSide;
    setScale(initial);
    setPos({ x: 0, y: 0 });
  };

  // ---------- CLAMPING ----------

  const clampPos = useCallback((nx, ny, s = scale) => {
    if (!imgNatural.w) return { x: nx, y: ny };
    const drawnW = imgNatural.w * s;
    const drawnH = imgNatural.h * s;
    // Image must always cover the crop box → translation limited so edges stay outside box bounds.
    const maxX = Math.max(0, (drawnW - boxPx) / 2);
    const maxY = Math.max(0, (drawnH - boxPx) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, nx)),
      y: Math.min(maxY, Math.max(-maxY, ny)),
    };
  }, [imgNatural, boxPx, scale]);

  const setScaleSafe = useCallback((s) => {
    const minS = imgNatural.w ? boxPx / Math.min(imgNatural.w, imgNatural.h) : MIN_SCALE;
    const next = Math.min(MAX_SCALE, Math.max(minS, s));
    setScale(next);
    setPos((p) => clampPos(p.x, p.y, next));
  }, [imgNatural, boxPx, clampPos]);

  // ---------- POINTER HANDLERS ----------

  const onPointerDown = (e) => {
    if (e.target.dataset?.role === 'handle') return;
    if (e.pointerType === 'touch' && e.isPrimary === false) return;
    cropAreaRef.current?.setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, startX: pos.x, startY: pos.y };
  };

  const onPointerMove = (e) => {
    if (!drag.current || pinch.current) return;
    const dx = e.clientX - drag.current.sx;
    const dy = e.clientY - drag.current.sy;
    setPos(clampPos(drag.current.startX + dx, drag.current.startY + dy));
  };

  const onPointerUp = (e) => {
    drag.current = null;
    cropAreaRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinch.current = { startDist: distance(e.touches[0], e.touches[1]), startScale: scale };
      drag.current = null;
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault();
      const d = distance(e.touches[0], e.touches[1]);
      setScaleSafe(pinch.current.startScale * (d / pinch.current.startDist));
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinch.current = null;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const dir = e.deltaY > 0 ? -1 : 1;
    setScaleSafe(scale + dir * 0.1);
  };

  // ---------- CROP / COMPRESS / UPLOAD ----------

  const renderToBlob = async () => {
    // Draw the visible crop region → outputSize×outputSize canvas → JPEG.
    const c = document.createElement('canvas');
    c.width = outputSize;
    c.height = outputSize;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingQuality = 'high';

    // What pixel rect of the SOURCE image lies under the crop box?
    // Crop box center in image-source coords:
    //   srcCenterX = naturalW/2 - pos.x/scale ; same for y.
    // Half-side in source coords = (boxPx / 2) / scale
    const halfSrc = (boxPx / 2) / scale;
    const srcCenterX = imgNatural.w / 2 - pos.x / scale;
    const srcCenterY = imgNatural.h / 2 - pos.y / scale;
    const sx = Math.max(0, srcCenterX - halfSrc);
    const sy = Math.max(0, srcCenterY - halfSrc);
    const sSize = halfSrc * 2;

    ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, outputSize, outputSize);

    // Iterative JPEG quality reduction to hit ≤maxKB.
    const limit = maxKB * 1024;
    let quality = 0.92;
    for (let i = 0; i < 6; i++) {
      const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', quality));
      if (!blob) throw new Error('Could not encode JPEG');
      if (blob.size <= limit || quality <= 0.5) return blob;
      quality -= 0.1;
    }
    return new Promise((res) => c.toBlob(res, 'image/jpeg', 0.5));
  };

  const upload = async () => {
    setBusy(true);
    setErr(null);
    setProgress(0);
    try {
      const blob = await renderToBlob();
      const form = new FormData();
      form.append('file', blob, 'profile.jpg');
      form.append('upload_preset', uploadPreset);
      form.append('folder', folder);

      // Native XHR so we can wire upload progress → Golden Hour bar.
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
          }
        };
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300) resolve(json);
            else reject(new Error(json?.error?.message || `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error('Invalid upload response'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload cancelled'));
        xhr.send(form);
      });

      setProgress(100);
      onUploaded(data.secure_url);
      onClose();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
      // Brief delay before clearing so users see the bar reach 100% on success.
      setTimeout(() => setProgress(0), 250);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0A1F2E]/90 flex items-stretch md:items-center md:justify-center md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Crop profile photo"
      data-testid="profile-crop-modal"
    >
      <div className="bg-[#F9F6EF] w-full md:max-w-md md:rounded-lg flex flex-col h-full md:h-auto md:max-h-[90vh] relative overflow-hidden">
        {/* Upload progress bar - Golden Hour, top edge, fades out on idle */}
        {busy && (
          <div
            className="absolute top-0 left-0 right-0 h-[3px] bg-[#F4ECD8] z-20"
            data-testid="profile-crop-progress"
            role="progressbar"
            aria-label="Upload progress"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full bg-[#E8A830] transition-[width] duration-200 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#F4ECD8] flex-shrink-0">
          <p className="font-mono uppercase text-[11px] tracking-widest text-[#1A5C6E] font-semibold">
            Crop profile photo
          </p>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 -mr-1.5 rounded-full hover:bg-[#F4ECD8]"
            aria-label="Close"
            data-testid="profile-crop-close"
          >
            <X className="w-5 h-5 text-[#0A1F2E]" />
          </button>
        </div>

        {/* Crop area - flex-1 on mobile, fixed on desktop */}
        <div className="flex-1 flex flex-col items-center justify-center py-4 px-3 min-h-0">
          <div
            ref={cropAreaRef}
            className="relative bg-[#0A1F2E] overflow-hidden touch-none select-none"
            style={{ width: `${boxPx}px`, height: `${boxPx}px` }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onWheel={onWheel}
            data-testid="profile-crop-area"
          >
            {imgUrl && (
              <img
                ref={imgRef}
                src={imgUrl}
                alt=""
                onLoad={onImgLoad}
                draggable={false}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                  transformOrigin: 'center center',
                  width: `${imgNatural.w || 0}px`,
                  height: `${imgNatural.h || 0}px`,
                  willChange: 'transform',
                  userSelect: 'none',
                  WebkitUserDrag: 'none',
                  pointerEvents: 'none',
                  maxWidth: 'none',
                }}
              />
            )}
            {/* Corner handles (visual only) */}
            <span data-role="handle" className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-[#E8A830]" />
            <span data-role="handle" className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-[#E8A830]" />
            <span data-role="handle" className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-[#E8A830]" />
            <span data-role="handle" className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-[#E8A830]" />
            {/* Grid overlay */}
            <span className="absolute inset-0 pointer-events-none border border-white/15" aria-hidden="true">
              <span className="absolute top-1/3 left-0 right-0 border-t border-white/10" />
              <span className="absolute top-2/3 left-0 right-0 border-t border-white/10" />
              <span className="absolute left-1/3 top-0 bottom-0 border-l border-white/10" />
              <span className="absolute left-2/3 top-0 bottom-0 border-l border-white/10" />
            </span>
          </div>

          {/* Zoom slider */}
          <div className="w-full max-w-sm mt-5 px-3 flex items-center gap-3" data-testid="profile-crop-zoom-row">
            <button
              type="button"
              onClick={() => setScaleSafe(scale - 0.2)}
              className="p-2 rounded-full bg-[#F4ECD8] text-[#0A1F2E]"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <input
              type="range"
              min={imgNatural.w ? boxPx / Math.min(imgNatural.w, imgNatural.h) : 1}
              max={MAX_SCALE}
              step={0.01}
              value={scale}
              onChange={(e) => setScaleSafe(parseFloat(e.target.value))}
              className="flex-1 h-2 accent-[#E8A830] touch-none"
              aria-label="Zoom"
              data-testid="profile-crop-zoom"
            />
            <button
              type="button"
              onClick={() => setScaleSafe(scale + 0.2)}
              className="p-2 rounded-full bg-[#F4ECD8] text-[#0A1F2E]"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => { setScale(boxPx / Math.min(imgNatural.w || 1, imgNatural.h || 1)); setPos({ x: 0, y: 0 }); }}
              className="p-2 rounded-full bg-[#F4ECD8] text-[#0A1F2E]"
              aria-label="Reset"
              data-testid="profile-crop-reset"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          <p className="mt-3 text-xs text-[#5A6B73] text-center px-4">
            Pinch to zoom · drag to reposition · ≤ {maxKB} KB after compression
          </p>
          {err && (
            <p className="mt-2 text-xs text-red-600 text-center px-4" role="alert" data-testid="profile-crop-error">
              {err}
            </p>
          )}
        </div>

        {/* Sticky bottom action bar - always visible above the fold */}
        <div
          className="flex-shrink-0 flex items-center gap-2 px-4 py-3 border-t border-[#F4ECD8] bg-[#F9F6EF]"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-3 text-sm font-mono uppercase tracking-wider font-semibold border border-[#0A1F2E]/15 text-[#0A1F2E] rounded-full hover:bg-[#0A1F2E]/5 transition disabled:opacity-50"
            data-testid="profile-crop-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={upload}
            disabled={busy || !imgUrl}
            className="flex-[1.4] inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-mono uppercase tracking-wider font-semibold bg-[#E8A830] text-[#0A1F2E] rounded-full hover:brightness-95 transition disabled:opacity-50"
            data-testid="profile-crop-confirm"
          >
            <Upload className="w-4 h-4" />
            {busy ? (progress > 0 ? `Uploading ${progress}%` : 'Uploading…') : 'Use photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * MobilePhotoUploadButton - small helper that wires together the file picker +
 * the crop modal. Drop-in replacement for `<button onClick={openCloudinaryWidget}>`.
 */
export const MobilePhotoUploadButton = ({
  cloudName,
  uploadPreset,
  folder,
  onUploaded,
  children,
  className,
  aspect = 1,
  maxKB = 500,
  title,
  // capture controls iOS/Android picker behavior:
  //   undefined / null → full picker (Photo Library + Take Photo + Browse) ← preferred
  //   "user" / "environment" → forces front or rear camera only
  // We default to undefined so existing call-sites get the friendlier picker
  // unless they explicitly request a direct-camera capture.
  capture,
  'data-testid': testId,
}) => {
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();
  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setOpen(true);
    }
    // Allow re-selecting the same file later.
    e.target.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        {...(capture ? { capture } : {})}
        onChange={onChange}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <button
        type="button"
        onClick={pick}
        className={className}
        data-testid={testId}
      >
        {children || (
          <>
            <Camera className="w-4 h-4" /> Upload photo
          </>
        )}
      </button>
      <MobilePhotoCropModal
        open={open}
        file={file}
        onClose={() => { setOpen(false); setFile(null); }}
        onUploaded={onUploaded}
        cloudName={cloudName}
        uploadPreset={uploadPreset}
        folder={folder}
        aspect={aspect}
        maxKB={maxKB}
        title={title}
      />
    </>
  );
};

export default MobilePhotoCropModal;
